import axios from "axios";

// API Configuration
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://api.dev.dz-metrics.com";

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem("refresh_token");
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken: refreshToken, // Backend expects 'refreshToken', not 'refresh_token'
          });

          const { token } = response.data; // Backend returns 'token', not 'access_token'
          localStorage.setItem("access_token", token);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

// Auth API
export const authAPI = {
  /**
   * @deprecated Password-based registration is deprecated. Use magic link authentication instead.
   * This endpoint will be removed in a future version. Use requestMagicLink() for passwordless registration.
   */
  register: (userData) => api.post("/auth/register", userData),

  /**
   * @deprecated Password-based login is deprecated. Use magic link authentication instead.
   * This endpoint will be removed in a future version. Use requestMagicLink() for passwordless login.
   */
  login: (credentials) => api.post("/auth/login", credentials),

  refresh: (refreshToken) =>
    api.post("/auth/refresh", { refreshToken: refreshToken }), // Backend expects 'refreshToken'
  logout: () => api.post("/auth/logout"),

  /**
   * @deprecated Password management is deprecated as part of passwordless migration.
   * These endpoints will be removed in a future version.
   */
  changePassword: (passwordData) =>
    api.post("/auth/change-password", passwordData),

  // Multi-Factor Authentication
  getMFAStatus: () => api.get("/auth/mfa/status"),
  startMFASetup: () => api.post("/auth/mfa/setup"),
  verifyMFASetup: (verificationData) =>
    api.post("/auth/mfa/verify", verificationData),
  disableMFA: () => api.post("/auth/mfa/disable"),
  regenerateBackupCodes: () => api.post("/auth/mfa/backup-codes/regenerate"),

  // Session Management
  getSessions: () => api.get("/auth/sessions"),
  revokeSession: (sessionId) => api.delete(`/auth/sessions/${sessionId}`),
  revokeAllOtherSessions: () => api.delete("/auth/sessions/others"),

  // Security Audit Log
  getAuditLog: (params) => api.get("/auth/audit-log", { params }),
  exportAuditLog: (params) =>
    api.get("/auth/audit-log/export", { params, responseType: "blob" }),

  // OAuth Identity Providers (Google, Microsoft, Facebook login)
  getOAuthProviders: () => api.get("/auth/oauth/providers"),
  googleAuthorize: (options = {}) =>
    api.post("/auth/google/authorize", {
      redirect_url: options.redirectUrl,
      action: options.action || "login",
      login_hint: options.loginHint,
    }),
  googleCallback: (code, state) =>
    api.post("/auth/google/callback", { code, state }),
  linkGoogle: (code, state) =>
    api.post("/auth/link/google", { code, state }),
  unlinkGoogle: () => api.delete("/auth/link/google"),

  // Magic Link Authentication (Passwordless) - RECOMMENDED
  requestMagicLink: (email, redirectUrl) =>
    api.post("/auth/magic-link/request", { email, redirect_url: redirectUrl }),
  verifyMagicLink: (token) =>
    api.post("/auth/magic-link/verify", { token }),
};

// User API
export const userAPI = {
  getProfile: () => api.get("/users/me"),
  updateProfile: (userData) => api.put("/users/me", userData),
  uploadAvatar: (avatarData) =>
    api.post("/users/me/avatar", { avatar: avatarData }),
  removeAvatar: () => api.delete("/users/me/avatar"),
  exportData: (format) =>
    api.post("/users/me/export", { format }, { responseType: "blob" }),
  deactivateAccount: () => api.post("/users/me/deactivate"),
  deleteAccount: () => api.delete("/users/me"),
};

// Platform API
export const platformAPI = {
  getConnections: () => api.get("/platforms"),
  initiateOAuth: (platform) =>
    api.post(`/platforms/connect`, { platform }),
  completeOAuth: (platform, code, state) =>
    api.get(`/platforms/callback/${platform}`, { params: { code, state } }),
  disconnectPlatform: (platformId) =>
    api.delete(`/platforms/${platformId}`),
};

// Social Accounts API
export const socialAccountsAPI = {
  getAccounts: () => api.get("/social-accounts"),
  getAccount: (accountId) => api.get(`/social-accounts/${accountId}`),
  disconnect: (accountId) => api.post(`/social-accounts/${accountId}/disconnect`),
  refresh: (accountId) => api.post(`/social-accounts/${accountId}/refresh`),
  getMetrics: (accountId, timeRange = "7d") =>
    api.get(`/social-accounts/${accountId}/metrics?time_range=${timeRange}`),
  refreshAll: () => api.post("/social-accounts/refresh-all"),
  getHealthStatus: () => api.get("/social-accounts/health"),
};

// OAuth API
export const oauthAPI = {
  getAuthUrl: (platform, scopes = null) => {
    const requestBody = { platform };
    if (scopes) {
      requestBody.scopes = Array.isArray(scopes) ? scopes : scopes.split(",");
    }
    return api.post("/oauth/authorize-url", requestBody);
  },
  handleCallback: (code, state, platform) =>
    api.post("/oauth/callback", { code, state, platform }),
  checkStatus: () => api.get("/oauth/check-status"),
  revoke: (accountId) => api.post(`/oauth/revoke/${accountId}`),
};

// Metrics API
export const metricsAPI = {
  getOverview: (timeRange = "7d") =>
    api.get(`/metrics/overview?time_range=${timeRange}`),
  getPlatformMetrics: (platform, timeRange = "7d") =>
    api.get(`/metrics/platforms/${platform}?time_range=${timeRange}`),
  getTimeline: (timeRange = "7d", platforms = []) => {
    const params = new URLSearchParams({ time_range: timeRange });
    platforms.forEach((platform) => params.append("platforms", platform));
    return api.get(`/metrics/timeline?${params}`);
  },
  getPostingTimes: (timeRange = "7d", platforms = []) => {
    const params = new URLSearchParams({ time_range: timeRange });
    platforms.forEach((platform) => params.append("platforms", platform));
    return api.get(`/metrics/posting-times?${params}`);
  },
};

// Campaign API
export const campaignAPI = {
  getCampaigns: (status = null, limit = 20, offset = 0) => {
    const params = new URLSearchParams({ limit, offset });
    if (status) params.append("status", status);
    return api.get(`/campaigns?${params}`);
  },
  getCampaign: (campaignId) => api.get(`/campaigns/${campaignId}`),
  createCampaign: (campaignData) => api.post("/campaigns", campaignData),
  updateCampaign: (campaignId, campaignData) =>
    api.put(`/campaigns/${campaignId}`, campaignData),
  deleteCampaign: (campaignId) => api.delete(`/campaigns/${campaignId}`),
};

// Reports API
export const reportsAPI = {
  getReports: (limit = 20, offset = 0) => {
    const params = new URLSearchParams({ limit, offset });
    return api.get(`/reports?${params}`);
  },
  getReport: (reportId) => api.get(`/reports/${reportId}`),
  generateReport: (reportData) => api.post("/reports/generate", reportData),
  getReportStatus: (reportId) => api.get(`/reports/${reportId}/status`),
  downloadReport: (reportId) =>
    api.get(`/reports/${reportId}/download`, { responseType: "blob" }),
};

// Subscription API
export const subscriptionAPI = {
  create: (subscriptionData) => api.post("/subscriptions", subscriptionData),
  get: (subscriptionId) => api.get(`/subscriptions/${subscriptionId}`),
  getCurrent: () => api.get("/subscriptions/current"),
  update: (subscriptionId, updateData) =>
    api.put(`/subscriptions/${subscriptionId}`, updateData),
  cancel: (subscriptionId, cancelData = {}) =>
    api.post(`/subscriptions/${subscriptionId}/cancel`, cancelData),
  resume: (subscriptionId) =>
    api.post(`/subscriptions/${subscriptionId}/resume`),
  updatePaymentMethod: (subscriptionId, paymentMethodData) =>
    api.put(
      `/subscriptions/${subscriptionId}/payment-method`,
      paymentMethodData,
    ),
  preview: (subscriptionData) =>
    api.post("/subscriptions/preview", subscriptionData),
};

// Billing API
export const billingAPI = {
  getHistory: (params = {}) => api.get("/billing/history", { params }),
  getInvoice: (invoiceId) => api.get(`/billing/invoices/${invoiceId}`),
  downloadInvoice: (invoiceId) =>
    api.get(`/billing/invoices/${invoiceId}/download`, {
      responseType: "blob",
    }),
  getUpcomingInvoice: () => api.get("/billing/upcoming-invoice"),
  getPaymentMethods: () => api.get("/billing/payment-methods"),
  addPaymentMethod: (paymentMethodData) =>
    api.post("/billing/payment-methods", paymentMethodData),
  removePaymentMethod: (paymentMethodId) =>
    api.delete(`/billing/payment-methods/${paymentMethodId}`),
  setDefaultPaymentMethod: (paymentMethodId) =>
    api.put(`/billing/payment-methods/${paymentMethodId}/default`),
};

// Stripe API
export const stripeAPI = {
  createPaymentIntent: (paymentData) =>
    api.post("/stripe/payment-intents", paymentData),
  createSetupIntent: (setupData) =>
    api.post("/stripe/setup-intents", setupData),
  getPublishableKey: () => api.get("/stripe/config"),
};

// Health API
export const healthAPI = {
  check: () => api.get("/health"),
};

// Utility functions
export const setAuthTokens = (accessToken, refreshToken) => {
  localStorage.setItem("access_token", accessToken);
  localStorage.setItem("refresh_token", refreshToken);
};

export const clearAuthTokens = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
};

export const isAuthenticated = () => {
  return !!localStorage.getItem("access_token");
};

export default api;
